import _defineProperty from "/app/node_modules/.pnpm/next@13.4.17_@babel+core@7.14.3_@opentelemetry+api@1.4.1_react-dom@18.2.0_react@18.2.0_sass@1.49.9/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { delay, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { all, call, select, take } from 'typed-redux-saga';
import { MEMBER_PROFILE_URL, ROOT_URL } from 'constants/routes';
import { Screen } from 'constants/tracking/screens';
import { EntryType } from 'constants/photo-tip';
import { ItemAfterUploadActions, ItemAlertStatus } from 'constants/item';
import { GoogleTagManagerEvent } from 'constants/google';
import { googleTagManagerTrack } from 'data/utils/google';
import * as api from 'data/api';
import { ResponseCode } from 'data/api/response-codes';
import { transformItemEditDto, transformBrandDtos, transformItemAuthenticityModalDto, transformVideoGameRatingDtos, transformPhotoTipDtos, transformSizeGroupDtos, transformPackageSizeShippingOptionDtos, transformDynamicAttributes, transformSupportedAttributesToSelectedValues } from 'data/transformers';
import { navigateToPage, scrollToElementById } from 'libs/utils/window';
import { tracker } from 'libs/common/tracker';
import { itemUploadStartEventExtra, itemUploadSubmitFail, itemUploadSubmitSuccess } from 'libs/common/event-tracker/events';
import * as sessionSelectors from 'state/session/selectors';
import { getIsItemSizeLabelOneAbTestEnabled, getAbTestByName } from 'state/ab-tests/selectors';
import * as mediaUploadSelectors from 'state/media-upload/selectors';
import { actions as mediaUploadActions } from 'state/media-upload/slice';
import { actions as packageSizesActions } from 'state/package-sizes/slice';
import { actions as itemActions } from 'state/items/slice';
import { UiState as UiStateEnum } from 'constants/ui';
import { ImageOrientation } from 'constants/images';
import { getIsFeatureSwitchEnabled } from 'state/feature-switches/selectors';
import { brazeLogCustomEvent } from 'libs/common/braze/utils/custom-event';
import { BrazeCustomEvent } from 'constants/braze';
import * as statelessActions from './actions';
import * as transformers from './transformers';
import * as selectors from './selectors';
import { REQUEST_DELAY, WITHOUT_BRAND_ID, FieldName, IsbnValidity, ItemUploadFailReason, SupportedDynamicAttributes } from './constants';
import { actions } from './slice';
import { setNewItemIdToStorage, setFeedbackItemIdToStorage, setPushedItemIdToStorage, getSupportedAttributesArray } from './helpers';

function* getIsSuggestedPackageSizeAbTestEnabled() {
  const suggestedPackageSizeAbTest = yield* select(getAbTestByName('parcel_size_suggestion_web'));
  return (suggestedPackageSizeAbTest === null || suggestedPackageSizeAbTest === void 0 ? void 0 : suggestedPackageSizeAbTest.variant) === 'on';
}

export function* dynamicAttributesChangeFlow() {
  const supportedAttributes = getSupportedAttributesArray({
    [SupportedDynamicAttributes.Material]: true,
    [SupportedDynamicAttributes.Gender]: true
  });
  const selectedValues = transformSupportedAttributesToSelectedValues(supportedAttributes);
  yield put(actions.setSelectedDynamicAttributeValues({
    selectedValues
  }));
}
export function* fetchDynamicAttributes(catalogId) {
  if (!catalogId) return;
  yield put(actions.setDynamicAttributesUiState({
    uiState: UiStateEnum.Pending
  }));
  const response = yield* call(api.getDynamicAttributes, {
    catalogId
  });

  if ('errors' in response) {
    yield put(actions.setCatalogId({
      id: null
    }));
    yield put(actions.fetchDynamicAttributesFailure());
    return;
  }

  yield put(actions.fetchDynamicAttributesSuccess({
    dynamicAttributes: transformDynamicAttributes(response.catalog_attributes)
  }));
}
export function* fetchItem(_ref) {
  let {
    payload: {
      id
    }
  } = _ref;
  yield put(statelessActions.fetchCatalogs());
  yield call(dynamicAttributesChangeFlow);
  const tempUuid = yield* select(selectors.getTempUuid);
  const sizeLabelAbTestEnabled = yield* select(getIsItemSizeLabelOneAbTestEnabled);
  if (sizeLabelAbTestEnabled) yield put(statelessActions.fetchSizesConfiguration());
  const userId = yield* select(sessionSelectors.getUserId);
  if (userId) yield put(statelessActions.getDropOffLocationPrompt({
    userId
  }));
  const configurationResponse = yield* call(api.getItemUploadConfiguration);
  if ('errors' in configurationResponse) return;
  yield put(actions.setConfiguration({
    configuration: configurationResponse
  }));

  if (!id) {
    const sessionResponse = yield* call(api.getSessionDefaults);
    if ('errors' in sessionResponse) return;
    yield put(actions.setCurrency({
      currency: sessionResponse.session_defaults_configuration.currency
    }));
    yield put(actions.setUiState({
      uiState: UiStateEnum.Success
    }));
    yield call(tracker.track, itemUploadStartEventExtra({
      tempUuid,
      screenName: Screen.ItemUpload
    }));
    return;
  }

  const response = yield* call(api.getItemEdit, {
    id
  });

  if ('errors' in response) {
    const isNextPageEnabled = yield* select(getIsFeatureSwitchEnabled('next_page_item_upload'));

    if (isNextPageEnabled) {
      yield put(actions.setUiState({
        uiState: UiStateEnum.Failure
      }));
    }

    return;
  }

  const item = transformItemEditDto(response.item);
  yield put(actions.setInitialItemData({
    item
  }));
  yield put(actions.setCurrency({
    currency: item.currency
  }));
  yield call(fetchDynamicAttributes, item.catalogId);
  const selectedValues = transformers.transformDynamicAttributes(item.itemAttributes);
  yield put(actions.setSelectedDynamicAttributeValues({
    selectedValues
  }));
  yield put(statelessActions.fetchPriceSuggestions());
  yield put(statelessActions.fetchItemOfflineVerificationEligibility());
  yield put(actions.setUiState({
    uiState: UiStateEnum.Success
  }));
  yield call(tracker.track, itemUploadStartEventExtra({
    tempUuid,
    screenName: item.isDraft ? Screen.ItemDraft : Screen.ItemEdit
  }));
}
export function* convertAssignedPhoto(id) {
  const photo = yield* select(mediaUploadSelectors.getPhoto, id);
  return {
    id,
    orientation: photo.orientation || ImageOrientation.Degree0
  };
}
export function* requestItemAuthenticityModal(_ref2) {
  let {
    payload
  } = _ref2;
  const {
    catalogId,
    brandId,
    id
  } = yield* select(selectors.selectAttributes);
  if (!brandId) return;
  const response = yield* call(api.getItemAuthenticityModal, catalogId, id, payload.force);
  if ('errors' in response) return;
  if (!response.authenticity_modal) return;
  const model = yield* call(transformItemAuthenticityModalDto, response.authenticity_modal);
  yield put(actions.setAuthenticityModalContent({
    content: model
  }));
  yield put(actions.setIsAuthenticityModalOpen({
    isOpen: true
  }));
}
export function* fetchBrands(_ref3) {
  let {
    payload
  } = _ref3;
  // Prevent request load so takeLatest effect would cancel saga between actual requests
  yield delay(200);
  const {
    keyword,
    includeAllBrands
  } = payload;
  const response = yield* call(api.getBrands, {
    keyword,
    includeAllBrands
  });
  if ('errors' in response) return;
  const brands = yield* call(transformBrandDtos, response.brands);
  yield put(actions.setBrands({
    brands
  }));
}
export function* selectCatalog(_ref4) {
  let {
    payload
  } = _ref4;
  const currentCatalog = yield* select(selectors.getCurrentCatalog);
  const catalog = yield* select(selectors.getCatalog, payload.catalogId);
  if (!catalog) return;

  if (catalog.restrictedToStatusId) {
    yield put(actions.setStatusId({
      id: null
    }));
  }

  if (!catalog.isIsbnInputShown) {
    yield put(actions.setIsbn({
      isbn: null,
      validity: IsbnValidity.Unvalidated
    }));
  }

  if (!catalog.isBrandSelectShown) {
    yield put(actions.setBrand({
      id: WITHOUT_BRAND_ID,
      title: null,
      isLuxury: false
    }));
  }

  if (currentCatalog) {
    if (currentCatalog.sizeGroupId !== catalog.sizeGroupId) {
      yield put(actions.setSizeId({
        id: null
      }));
    }
  }

  yield put(actions.setCatalogId({
    id: payload.catalogId
  }));
  yield call(fetchDynamicAttributes, payload.catalogId);
  const brandIsLuxury = yield* select(selectors.getIsBrandLuxury);

  if (brandIsLuxury) {
    yield put(statelessActions.requestItemAuthenticityModal());
  }
}
export function* fetchSizeGroups() {
  const response = yield* call(api.getSizeGroups);
  if ('errors' in response) return;
  const transformedDto = yield* call(transformSizeGroupDtos, response.size_groups);
  yield put(actions.setSizeGroups({
    sizeGroups: transformedDto.sizeGroups
  }));
  yield put(actions.setSizes({
    sizes: transformedDto.sizes
  }));
}
export function* fetchVideoGameRatings() {
  const response = yield* call(api.getVideoGameRatings);
  if ('errors' in response) return;
  const transformedDto = yield* call(transformVideoGameRatingDtos, response.video_game_ratings);
  yield put(actions.setVideoGameRatings(transformedDto));
}
export function* fetchCatalogs() {
  const response = yield* call(api.getCatalogs);
  if ('errors' in response) return;
  const catalogs = yield* call(transformers.transformCatalogs, response.catalogs);
  yield put(actions.setCatalogs({
    catalogs
  }));
}
export function* fetchColors() {
  const response = yield* call(api.requestColors);

  if ('errors' in response) {
    yield delay(REQUEST_DELAY);
    yield put(actions.fetchColorsRequest());
    return;
  }

  const colors = yield* call(transformers.transformColors, response.colors);
  yield put(actions.fetchColorsSuccess({
    colors
  }));
}
export function* fetchStatuses() {
  const response = yield* call(api.getStatuses);
  if ('errors' in response) return;
  const statuses = yield* call(transformers.transformStatuses, response.statuses);
  yield put(actions.setStatuses({
    statuses
  }));
}
export function* fetchPriceSuggestions() {
  const {
    catalogId,
    statusId,
    brandId
  } = yield* select(selectors.selectAttributes);

  if (!brandId || !statusId || !catalogId) {
    yield put(actions.clearPriceSuggestions());
    return;
  }

  const response = yield* call(api.getItemPriceSuggestions, {
    brandId,
    statusId,
    catalogId
  });

  if ('errors' in response) {
    yield put(actions.clearPriceSuggestions());
    return;
  }

  const suggestions = transformers.transformPriceSuggestionsDto(response);
  yield put(actions.setPriceSuggestions({
    suggestions
  }));
}
export function* toggleSimilarItemsModal() {
  const isSimilarItemsModalOpen = yield* select(selectors.getIsSimilarItemsModalOpen);

  if (isSimilarItemsModalOpen) {
    const {
      catalogId,
      statusId,
      brandId
    } = yield* select(selectors.selectAttributes);

    if (!brandId || !statusId || !catalogId) {
      yield put(itemActions.resetSimilarItems());
      return;
    }

    yield put(itemActions.getSimilarItemsRequest({
      brandId,
      statusId,
      catalogId
    }));
  } else {
    yield put(itemActions.resetSimilarItems());
  }
}
export function* fetchBookDetails(_ref5) {
  let {
    payload
  } = _ref5;
  if (payload.validity === IsbnValidity.Unvalidated) return;

  if (payload.validity === IsbnValidity.Invalid || !payload.isbn) {
    yield put(actions.removeFieldError({
      fieldName: FieldName.Isbn
    }));
    return;
  }

  yield put(actions.setIsbnPendingState());
  yield delay(REQUEST_DELAY);
  const response = yield* call(api.getBookDetails, {
    isbn: payload.isbn
  });

  if ('errors' in response) {
    yield put(actions.setFieldError({
      fieldError: {
        field: FieldName.Isbn,
        value: response.message
      }
    }));
    return;
  }

  const bookDetails = transformers.transformBookDetails(response);
  yield put(actions.setBookDetails({
    bookDetails
  }));
}
export function* fetchPackageSizes() {
  const {
    catalogId,
    price: priceValue,
    brandId
  } = yield* select(selectors.selectAttributes);
  const currency = yield* select(selectors.getCurrency);
  const price = priceValue ? {
    amount: priceValue.toString(),
    currency_code: currency
  } : undefined;
  if (!catalogId) return;
  yield put(packageSizesActions.fetchCatalogPackageSizesRequest({
    catalogId,
    brandId: brandId || undefined,
    price
  }));
}
export function* fetchSuggestedPackageSizeCode() {
  yield delay(REQUEST_DELAY);
  const isSuggestedPackageSizeCodeEnabled = yield* call(getIsSuggestedPackageSizeAbTestEnabled);
  if (!isSuggestedPackageSizeCodeEnabled) return;
  const itemAttributes = yield* select(selectors.selectAttributes);
  if (!itemAttributes.catalogId) return;
  const assignedPhotos = yield* all(itemAttributes.assignedPhotos.map(photoId => call(convertAssignedPhoto, photoId)));
  const dynamicAttributes = yield* select(selectors.getSelectedDynamicAttributes);
  const currency = yield* select(selectors.getCurrency);
  const itemDto = yield* call(transformers.transformItemAttributesToItemUploadDto, itemAttributes, assignedPhotos, dynamicAttributes, currency || '', '');
  const response = yield* call(api.getSuggestedPackageSizeCode, {
    item: itemDto
  });

  if ('errors' in response) {
    yield put(packageSizesActions.fetchSuggestedPackageSizeCodeFailure({
      errors: response.errors
    }));
    return;
  }

  yield put(packageSizesActions.fetchSuggestedPackageSizeCodeSuccess({
    suggestedPackageSizeCode: response.package_size_code
  }));
}
export function* toggleShippmentOptionsModal() {
  const isShipmentOptionsModalOpen = yield* select(selectors.getIsShippingOptionsModalOpen);
  if (!isShipmentOptionsModalOpen) return;
  yield put(statelessActions.fetchShippingOptionsRequest());
}
export function* deleteDraft() {
  const {
    id
  } = yield* select(selectors.selectAttributes);
  if (!id) return;
  yield put(actions.setIsFormDisabled({
    isFormDisabled: true
  }));
  yield* call(api.deleteItemDraft, id);
  const userId = yield* select(sessionSelectors.getUserId);
  const redirectUrl = userId ? MEMBER_PROFILE_URL(userId) : ROOT_URL;
  yield* call(navigateToPage, redirectUrl);
}
export function* handleNewItemIncompleteTaxAddressResponse(_ref6) {
  var _action$payload;

  let {
    meta
  } = _ref6;
  yield put(actions.showMissingPostalCode());
  const action = yield* take(actions.hideMissingPostalCode);
  if (!((_action$payload = action.payload) !== null && _action$payload !== void 0 && _action$payload.resubmitItem)) return;
  yield put(statelessActions.submitItem(meta.saveAsDraft, meta.isItemPushedUp));
}
export function* handleItemSubmitResponseCode(_ref7) {
  let {
    responseCode,
    meta
  } = _ref7;

  switch (responseCode) {
    case ResponseCode.IncompleteTaxAddress:
      yield* call(handleNewItemIncompleteTaxAddressResponse, {
        meta
      });
      break;

    case ResponseCode.PhotoMinimumCountRequired:
      yield put(actions.setIsLuxuryItemModalOpen({
        isOpen: true
      }));
      break;

    default:
      break;
  }
}
export function* handleReplicaProof(response) {
  var _response$after_uploa;

  yield put(actions.setIsAuthenticityProofModalOpen({
    isOpen: true
  }));
  yield take(actions.setIsAuthenticityProofModalOpen);

  if ((_response$after_uploa = response.after_upload_actions) !== null && _response$after_uploa !== void 0 && _response$after_uploa.includes(ItemAfterUploadActions.ShowOfflineVerificationModal)) {
    yield put(actions.setIsOfflineVerificationModalOpen({
      isOpen: true
    }));
    return;
  }

  yield* call(navigateToPage, MEMBER_PROFILE_URL(response.item.user_id));
}
export function* trackListingToGoogleTagManager(item) {
  const isGtmEcFieldEnabled = yield* select(getIsFeatureSwitchEnabled('web_gtm_ec_field'));
  const event = {
    item_name: item.title,
    item_id: item.id,
    value: parseFloat(item.price.amount),
    currency: item.currency,
    user_email: item.user.email
  };
  yield call(googleTagManagerTrack, GoogleTagManagerEvent.ItemList, _objectSpread(_objectSpread({}, event), {}, {
    item_category_id: item.catalog_id,
    item_brand: item.brand,
    item_brand_id: item.brand_id,
    item_condition: item.disposal_conditions
  }), isGtmEcFieldEnabled);
  const isPreviousLister = yield* select(selectors.getIsPreviousLister);

  if (!isPreviousLister) {
    yield call(googleTagManagerTrack, GoogleTagManagerEvent.FirstList, event, isGtmEcFieldEnabled);
  }

  const isSecondDayLister = yield* select(selectors.getIsSecondDayLister);

  if (isSecondDayLister) {
    yield call(googleTagManagerTrack, GoogleTagManagerEvent.SecondDayList, event, isGtmEcFieldEnabled);
  }
}
export function* trackListingToBraze(item, screenName) {
  yield call(brazeLogCustomEvent, {
    event: BrazeCustomEvent.ListingCreated,
    modelId: item.id,
    userExternalId: item.user.external_id || null,
    screen: screenName
  }, {
    defer: true
  });
}
export function* submitItem(_ref8) {
  var _response$after_uploa2;

  let {
    meta
  } = _ref8;
  yield put(actions.setIsFormDisabled({
    isFormDisabled: true
  }));
  let response;
  const tempUuid = yield* select(selectors.getTempUuid);
  const screenName = yield* select(selectors.getScreenName);
  const itemAttributes = yield* select(selectors.selectAttributes);
  const assignedPhotos = yield* all(itemAttributes.assignedPhotos.map(photoId => call(convertAssignedPhoto, photoId)));
  const dynamicAttributes = yield* select(selectors.getSelectedDynamicAttributes);
  const currency = yield* select(selectors.getCurrency);
  const itemDto = yield* call(transformers.transformItemAttributesToItemUploadDto, itemAttributes, assignedPhotos, dynamicAttributes, currency, tempUuid);
  const {
    isItemPushedUp,
    saveAsDraft
  } = meta;

  if (saveAsDraft) {
    if (itemDto.id) {
      response = yield* call(api.updateItemDraft, itemDto.id, itemDto, itemAttributes.feedbackId);
    } else {
      response = yield* call(api.createItemDraft, itemDto, itemAttributes.feedbackId);
    }
  } else if (screenName === Screen.ItemDraft && itemDto.id) {
    response = yield* call(api.completeItem, itemDto.id, itemDto, itemAttributes.feedbackId);

    if (isItemPushedUp) {
      setPushedItemIdToStorage(response);
    } else {
      setNewItemIdToStorage(response);
    }
  } else if (itemDto.id) {
    response = yield* call(api.updateItem, itemDto.id, itemDto, itemAttributes.feedbackId);

    if (isItemPushedUp) {
      setPushedItemIdToStorage(response);
    }

    if ('item' in response) {
      var _item$item_alert;

      const {
        item
      } = response;
      const previousAlert = yield* select(selectors.getPreviousAlertType);

      if (previousAlert === ItemAlertStatus.ReplicaProof && ((_item$item_alert = item.item_alert) === null || _item$item_alert === void 0 ? void 0 : _item$item_alert.item_alert_type) === ItemAlertStatus.UnderReview) {
        yield call(handleReplicaProof, response);
        return;
      }
    }
  } else {
    response = yield* call(api.createItem, itemDto, itemAttributes.feedbackId);

    if (isItemPushedUp) {
      setPushedItemIdToStorage(response);
    } else {
      setNewItemIdToStorage(response);
    }
  }

  if (!itemAttributes.feedbackId) {
    setFeedbackItemIdToStorage(response);
  }

  if (!response) {
    yield put(actions.setIsFormDisabled({
      isFormDisabled: false
    }));
    return;
  }

  if ('errors' in response) {
    var _response$errors$;

    yield put(actions.setFieldErrors({
      fieldErrors: response.errors
    }));
    yield* call(scrollToElementById, (_response$errors$ = response.errors[0]) === null || _response$errors$ === void 0 ? void 0 : _response$errors$.field);
    yield put(actions.setIsFormDisabled({
      isFormDisabled: false
    }));
    tracker.track(itemUploadSubmitFail({
      reason: ItemUploadFailReason.ValidationError,
      validationErrors: response.errors.map(error => error.value),
      screen: screenName,
      tempUuid
    }));
    yield call(handleItemSubmitResponseCode, {
      responseCode: response.code,
      meta
    });
    return;
  }

  let redirectUrl = ROOT_URL;
  let itemId = itemDto.id;

  if ('item' in response) {
    const {
      item
    } = response;
    const isItemEdit = screenName === Screen.ItemEdit;
    const isNewListing = !saveAsDraft && !isItemEdit;

    if (isNewListing) {
      yield call(trackListingToGoogleTagManager, item);
      yield call(trackListingToBraze, item, screenName);
    }

    redirectUrl = MEMBER_PROFILE_URL(item.user_id);
    itemId = item.id;
  } else if ('draft' in response) {
    redirectUrl = MEMBER_PROFILE_URL(response.draft.user_id);
    itemId = response.draft.id;
  }

  tracker.track(itemUploadSubmitSuccess({
    itemId: itemId || undefined,
    tempUuid,
    screen: screenName
  }));

  if ((_response$after_uploa2 = response.after_upload_actions) !== null && _response$after_uploa2 !== void 0 && _response$after_uploa2.includes(ItemAfterUploadActions.ShowOfflineVerificationModal)) {
    yield put(actions.setIsOfflineVerificationModalOpen({
      isOpen: true
    }));
    return;
  }

  yield* call(navigateToPage, redirectUrl);
}
export function* fetchPhotoTips() {
  const response = yield* call(api.getPhotoTips, EntryType.NewItem);
  if ('errors' in response) return;
  const photoTipsModel = yield* call(transformPhotoTipDtos, response.photo_tips);
  yield put(actions.setPhotoTips({
    photoTips: photoTipsModel
  }));
}
export function* fetchItemSuggestions() {
  yield delay(REQUEST_DELAY);
  const {
    title,
    description,
    catalogId
  } = yield* select(selectors.selectAttributes);
  const assignedPhotos = yield* select(selectors.getAssignedPhotos);
  const args = {
    title,
    description,
    catalogId,
    photoIds: assignedPhotos
  };
  const response = yield* call(api.getItemSuggestions, args);
  if ('errors' in response) return;
  const {
    colors,
    sizes,
    brands,
    catalogs
  } = yield* call(transformers.transformItemSuggestions, response);
  yield put(actions.setSuggestions({
    colors,
    sizes,
    brands,
    catalogs
  }));
}
export function* fetchShippingOptions() {
  const {
    catalogId,
    price: priceValue,
    brandId,
    packageSize
  } = yield* select(selectors.selectAttributes);
  const currency = yield* select(selectors.getCurrency);
  const price = priceValue ? {
    amount: priceValue.toString(),
    currency_code: currency
  } : undefined;
  if (!packageSize.id || !catalogId) return;
  const response = yield* call(api.getPackageShippingOptions, {
    packageSizeId: packageSize.id,
    catalogId,
    brandId: brandId || undefined,
    price
  });

  if ('errors' in response) {
    yield put(actions.fetchShippingOptionsFailure());
    return;
  }

  const shippingOptions = transformPackageSizeShippingOptionDtos(response.shipping_options);
  yield put(actions.fetchShippingOptionsSuccess({
    shippingOptions
  }));
}
export function* fetchSizesConfiguration() {
  const response = yield* call(api.getSizesConfiguration);

  if ('errors' in response) {
    yield put(actions.setShouldShowSizeModal({
      shouldShowSizeModal: false
    }));
    return;
  }

  yield put(actions.setShouldShowSizeModal({
    shouldShowSizeModal: response.require_size_check_reminder
  }));
}
export function* fetchItemOfflineVerificationEligibility() {
  yield delay(REQUEST_DELAY);
  const {
    catalogId,
    price,
    brandId
  } = yield* select(selectors.selectAttributes);

  if (!brandId || !price || !catalogId) {
    yield put(actions.setIsOfflineVerificationEligible({
      eligible: false
    }));
    return;
  }

  const response = yield* call(api.getItemOfflineVerificationEligibility, {
    brandId,
    price,
    catalogId
  });

  if ('errors' in response) {
    yield put(actions.setIsOfflineVerificationEligible({
      eligible: false
    }));
    return;
  }

  yield put(actions.setIsOfflineVerificationEligible({
    eligible: response.eligible
  }));
}
export function* fetchIsSecondDayLister() {
  const response = yield* call(api.getIsSecondDayLister);

  if ('errors' in response) {
    yield put(actions.setIsSecondDayLister({
      isSecondDayLister: false
    }));
    return;
  }

  yield put(actions.setIsSecondDayLister({
    isSecondDayLister: response.value
  }));
}
export function* fetchIsPreviousLister() {
  const response = yield* call(api.getIsPreviousLister);

  if ('errors' in response) {
    yield put(actions.setIsPreviousLister({
      isPreviousLister: false
    }));
    return;
  }

  yield put(actions.setIsPreviousLister({
    isPreviousLister: response.value
  }));
}
export default function* saga() {
  yield takeLatest([actions.setTitle, actions.setDescription, actions.setCatalogId, mediaUploadActions.uploadPhotoSuccess, mediaUploadActions.fetchPhotosSuccess], fetchItemSuggestions);
  yield takeLatest(statelessActions.fetchItemRequest, fetchItem);
  yield takeLatest(statelessActions.fetchIsSecondDayListerRequest, fetchIsSecondDayLister);
  yield takeLatest(statelessActions.fetchIsPreviousListerRequest, fetchIsPreviousLister);
  yield takeLatest(statelessActions.requestItemAuthenticityModal, requestItemAuthenticityModal);
  yield takeEvery(statelessActions.selectCatalog, selectCatalog);
  yield takeEvery(actions.fetchColorsRequest, fetchColors);
  yield takeEvery(statelessActions.fetchStatuses, fetchStatuses);
  yield takeEvery(statelessActions.fetchSizeGroups, fetchSizeGroups);
  yield takeEvery(statelessActions.fetchVideoGameRatings, fetchVideoGameRatings);
  yield takeLatest(statelessActions.fetchBrands, fetchBrands);
  yield takeLatest(statelessActions.fetchPhotoTips, fetchPhotoTips);
  yield takeEvery(statelessActions.deleteDraft, deleteDraft);
  yield takeEvery(statelessActions.submitItem, submitItem);
  yield takeLatest([statelessActions.fetchPriceSuggestions, actions.setBrand, actions.setStatusId, actions.setCatalogId], fetchPriceSuggestions);
  yield takeLatest(actions.toggleIsSimilarItemsModalOpen, toggleSimilarItemsModal);
  yield takeLatest(actions.setIsbn, fetchBookDetails);
  yield takeLatest([actions.setCatalogId, statelessActions.fetchPackageSizesRequest], fetchPackageSizes);
  yield takeLatest([actions.setTitle, actions.setDescription, actions.setCatalogId, actions.setPrice, mediaUploadActions.uploadPhotoSuccess, mediaUploadActions.fetchPhotosSuccess, actions.setBrand, actions.setStatusId, actions.setColorIds, actions.setSizeId], fetchSuggestedPackageSizeCode);
  yield takeLatest(actions.toggleIsShippingOptionsModalOpen, toggleShippmentOptionsModal);
  yield takeLatest(statelessActions.fetchShippingOptionsRequest, fetchShippingOptions);
  yield takeLatest(statelessActions.fetchCatalogs, fetchCatalogs);
  yield takeEvery(statelessActions.fetchSizesConfiguration, fetchSizesConfiguration);
  yield takeLatest([statelessActions.fetchItemOfflineVerificationEligibility, actions.setBrand, actions.setPrice, actions.setCatalogId], fetchItemOfflineVerificationEligibility);
}